import React, { useState, useRef, useEffect } from 'react';
import ServerSettings from './ServerSettings';
import UserProfileEdit from './UserProfileEdit';
import Settings from './Settings';
import ChannelPermissions from './ChannelPermissions';
import { useNotification } from './NotificationSystem';
import './ChannelList.css';

function InviteModal({ server, onClose, user }) {
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Generate invite when modal opens
    const generateInvite = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/invites`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ serverId: server.id })
        });
        
        if (response.ok) {
          const data = await response.json();
          setInviteCode(data.code);
        } else {
          console.error('Failed to generate invite');
        }
      } catch (error) {
        console.error('Error generating invite:', error);
      } finally {
        setLoading(false);
      }
    };
    
    generateInvite();
  }, [server.id]);

  const copyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/invite/${inviteCode}`;
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inviteUrl = `${window.location.origin}/invite/${inviteCode}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invite friends to {server.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading">Generating invite link...</div>
          ) : (
            <>
              <div className="invite-info">
                <p>Share this link with your friends to invite them to this server.</p>
                <p className="invite-expiry">This link expires in 24 hours.</p>
              </div>
              <div className="invite-link-container">
                <input 
                  type="text" 
                  value={inviteUrl} 
                  readOnly 
                  className="invite-link-input"
                />
                <button 
                  className="copy-button"
                  onClick={copyInviteLink}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ContextMenu({ x, y, onClose, items }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div 
      ref={menuRef}
      className="context-menu" 
      style={{ top: y, left: x }}
    >
      {items.map((item, idx) => (
        <div 
          key={idx}
          className={`context-menu-item ${item.danger ? 'danger' : ''}`}
          onClick={() => {
            item.onClick();
            onClose();
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
}

function VoiceMemberItem({ voiceUser, user, voiceAudioControls }) {
  const [showControls, setShowControls] = useState(false);
  const [volume, setVolume] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false);

  const isSelf = voiceUser.userId === user.id;

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (voiceAudioControls?.setVolume) {
      voiceAudioControls.setVolume(voiceUser.userId, newVolume);
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (voiceAudioControls?.toggleMute) {
      voiceAudioControls.toggleMute(voiceUser.userId);
    }
  };

  return (
    <div className="voice-member-sidebar-wrapper">
      <div 
        className={`voice-member-sidebar ${isSelf ? 'self' : ''}`}
        onClick={() => !isSelf && setShowControls(!showControls)}
      >
        <div className="voice-member-sidebar-avatar">
          {voiceUser.username[0].toUpperCase()}
        </div>
        <span className="voice-member-sidebar-name">{voiceUser.username}</span>
      </div>
      {showControls && !isSelf && voiceAudioControls && (
        <div className="voice-member-sidebar-controls">
          <div className="voice-control-item">
            <label>Volume</label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              className="volume-slider"
            />
            <span className="volume-label">{Math.round(volume * 100)}%</span>
          </div>
          <button 
            className="voice-control-button"
            onClick={handleMuteToggle}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </button>
        </div>
      )}
    </div>
  );
}

function ChannelList({ server, channels, currentChannel, onSelectChannel, user, onLogout, unreadCounts = {}, mentionCounts = {}, voiceUsers = {}, socket, voiceAudioControls, showMobile, onToggleMobileMenu }) {
  const { showSuccess, showError, confirm, prompt, choice } = useNotification();
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);
  const [newChannelName, setNewChannelName] = useState('');
  const [canManageChannels, setCanManageChannels] = useState(false);
  const [showServerMenu, setShowServerMenu] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showServerSettings, setShowServerSettings] = useState(false);
  const [showUserProfileEdit, setShowUserProfileEdit] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showChannelPermissions, setShowChannelPermissions] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [draggingChannel, setDraggingChannel] = useState(null);
  const [dragOverChannel, setDragOverChannel] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const serverMenuRef = useRef(null);

  // Check if user has MANAGE_CHANNELS permission
  useEffect(() => {
    const checkPermissions = async () => {
      if (!server || !user) {
        console.log('No server or user for permission check');
        setCanManageChannels(false);
        return;
      }
      
      console.log('Permission check:', { 
        serverOwnerId: server.owner_id, 
        userId: user.id, 
        match: server.owner_id === user.id 
      });
      
      // Server owner always has permissions
      if (server.owner_id === user.id || server.ownerId === user.id) {
        console.log('User is server owner, granting permissions');
        setCanManageChannels(true);
        return;
      }

      setCanManageChannels(false);
    };

    checkPermissions();
  }, [server, user]);

  // Group channels by category
  const channelsByCategory = {};
  
  if (channels && Array.isArray(channels)) {
    channels
      .sort((a, b) => (a.position || 0) - (b.position || 0)) // Sort by position first
      .forEach(channel => {
        const category = channel.category || (channel.type === 'text' ? 'TEXT CHANNELS' : 'VOICE CHANNELS');
        if (!channelsByCategory[category]) {
          channelsByCategory[category] = [];
        }
        channelsByCategory[category].push(channel);
      });
  }

  // Sort categories: TEXT CHANNELS first, VOICE CHANNELS second, then alphabetically
  const getSortedCategories = () => {
    const categories = Object.keys(channelsByCategory);
    return categories.sort((a, b) => {
      if (a === 'TEXT CHANNELS') return -1;
      if (b === 'TEXT CHANNELS') return 1;
      if (a === 'VOICE CHANNELS') return -1;
      if (b === 'VOICE CHANNELS') return 1;
      return a.localeCompare(b);
    });
  };

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleChannelRightClick = (e, channel) => {
    e.preventDefault();
    
    // Only show context menu if user has permissions
    if (!canManageChannels) return;
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: 'Edit Permissions',
          onClick: () => {
            setSelectedChannel(channel);
            setShowChannelPermissions(true);
          }
        },
        {
          label: 'Rename Channel',
          onClick: () => {
            setEditingChannel(channel.id);
            setNewChannelName(channel.name);
          }
        },
        {
          label: 'Delete Channel',
          danger: true,
          onClick: () => handleDeleteChannel(channel.id)
        }
      ]
    });
  };

  const handleAddChannel = async (category, channelType = null) => {
    // Check permissions before allowing
    if (!canManageChannels) {
      showError('You do not have permission to manage channels');
      return;
    }
    
    const channelName = await prompt('Enter new channel name:', 'Create Channel');
    if (!channelName || !channelName.trim()) return;

    // Determine type: use passed type, or infer from category name
    const type = channelType || (category.includes('TEXT') ? 'text' : 'voice');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/servers/${server.id}/channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: channelName.trim(), type, category })
      });

      if (!response.ok) {
        const error = await response.json();
        showError(error.error || 'Failed to create channel');
      }
    } catch (error) {
      console.error('Error creating channel:', error);
      showError('Failed to create channel');
    }
  };

  const handleRenameChannel = async (channelId) => {
    if (!newChannelName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/channels/${channelId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newChannelName.trim() })
      });

      if (!response.ok) {
        const error = await response.json();
        showError(error.error || 'Failed to rename channel');
      }
      setEditingChannel(null);
    } catch (error) {
      console.error('Error renaming channel:', error);
      showError('Failed to rename channel');
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!await confirm('Are you sure you want to delete this channel?', 'Delete Channel')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/channels/${channelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        showError(error.error || 'Failed to delete channel');
      }
    } catch (error) {
      console.error('Error deleting channel:', error);
      showError('Failed to delete channel');
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, channel) => {
    if (!canManageChannels) return;
    setDraggingChannel(channel);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, channel) => {
    if (!canManageChannels || !draggingChannel) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverChannel(channel);
  };

  const handleDragOverCategory = (e, category) => {
    if (!canManageChannels || !draggingChannel) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategory(category);
  };

  const handleDrop = async (e, targetChannel, targetCategory) => {
    e.preventDefault();
    if (!canManageChannels || !draggingChannel) return;

    const category = targetCategory || targetChannel.category;
    const categoryChannels = channelsByCategory[category] || [];
    
    // Create new channel list with updated positions
    const reorderedChannels = [];
    let newPosition = 0;

    // Add all channels from all categories with updated positions
    getSortedCategories().forEach((cat) => {
      const catChannels = channelsByCategory[cat];
      catChannels.forEach(ch => {
        if (ch.id === draggingChannel.id) {
          // Skip dragging channel for now
          return;
        }
        
        // If this is the target channel, insert dragged channel before it
        if (targetChannel && ch.id === targetChannel.id && cat === category) {
          reorderedChannels.push({
            id: draggingChannel.id,
            position: newPosition++,
            category: category
          });
        }
        
        reorderedChannels.push({
          id: ch.id,
          position: newPosition++,
          category: cat
        });
      });
    });

    // If dropped on category header (no target channel), add to end of category
    if (!targetChannel && targetCategory) {
      reorderedChannels.push({
        id: draggingChannel.id,
        position: newPosition++,
        category: targetCategory
      });
    }

    // Send update to server
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_API_URL}/api/servers/${server.id}/channels/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ channels: reorderedChannels })
      });
    } catch (error) {
      console.error('Error reordering channels:', error);
      showError('Failed to reorder channels');
    }

    setDraggingChannel(null);
    setDragOverChannel(null);
    setDragOverCategory(null);
  };

  const handleDragEnd = () => {
    setDraggingChannel(null);
    setDragOverChannel(null);
    setDragOverCategory(null);
  };

  // Category Management
  const handleCreateCategory = async () => {
    const categoryName = await prompt('Enter new category name:', 'Create Category');
    if (!categoryName || !categoryName.trim()) return;
    
    // Ask user what type of channel to create using radio buttons
    const channelType = await choice(
      'Select the type of channel to create in this category:',
      'Choose Channel Type',
      [
        { label: 'Text Channel', value: 'text' },
        { label: 'Voice Channel', value: 'voice' }
      ]
    );
    
    if (!channelType) return; // User cancelled
    
    // Create a channel in the new category to establish it
    await handleAddChannel(categoryName.trim(), channelType);
  };

  const handleRenameCategory = async (oldCategoryName) => {
    const newName = newCategoryName.trim();
    if (!newName || newName === oldCategoryName) {
      setEditingCategory(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const categoryChannels = channelsByCategory[oldCategoryName] || [];
      
      // Update all channels in this category
      const updatePromises = categoryChannels.map(channel =>
        fetch(`${process.env.REACT_APP_API_URL}/api/channels/${channel.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ category: newName })
        })
      );

      await Promise.all(updatePromises);
      setEditingCategory(null);
      setNewCategoryName('');
      showSuccess('Category renamed successfully');
    } catch (error) {
      console.error('Error renaming category:', error);
      showError('Failed to rename category');
    }
  };

  const handleCategoryRightClick = (e, category) => {
    e.preventDefault();
    if (!canManageChannels) return;

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: 'Rename Category',
          onClick: () => {
            setEditingCategory(category);
            setNewCategoryName(category);
          }
        },
        {
          label: 'Delete Category',
          danger: true,
          onClick: async () => {
            const confirmed = await confirm(
              `Delete category "${category}"? All channels will be moved to default categories.`,
              'Delete Category'
            );
            if (!confirmed) return;

            try {
              const token = localStorage.getItem('token');
              const categoryChannels = channelsByCategory[category] || [];
              
              // Move all channels to default category
              const updatePromises = categoryChannels.map(channel =>
                fetch(`${process.env.REACT_APP_API_URL}/api/channels/${channel.id}`, {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ 
                    category: channel.type === 'text' ? 'TEXT CHANNELS' : 'VOICE CHANNELS'
                  })
                })
              );

              await Promise.all(updatePromises);
              showSuccess('Category deleted successfully');
            } catch (error) {
              console.error('Error deleting category:', error);
              showError('Failed to delete category');
            }
          }
        }
      ]
    });
  };

  // Close server menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (serverMenuRef.current && !serverMenuRef.current.contains(e.target)) {
        setShowServerMenu(false);
      }
    };
    if (showServerMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showServerMenu]);

  return (
    <div className={`channel-list ${showMobile ? 'mobile-visible' : ''}`}>
      <div className="server-header" onClick={() => setShowServerMenu(!showServerMenu)} ref={serverMenuRef}>
        <div className="server-name">{server?.name || 'Select a server'}</div>
        <div className="dropdown-icon">▼</div>
        {showServerMenu && (
          <div className="server-dropdown-menu" onClick={(e) => e.stopPropagation()}>
            <div className="server-dropdown-item" onClick={() => {
              setShowInviteModal(true);
              setShowServerMenu(false);
            }}>
              <span>Invite People</span>
            </div>
            {canManageChannels && (
              <div className="server-dropdown-item danger" onClick={() => {
                setShowServerMenu(false);
                setShowServerSettings(true);
              }}>
                <span>Server Settings</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="channels-container">
        {canManageChannels && (
          <button 
            className="create-category-button"
            onClick={() => handleCreateCategory()}
            title="Create Category"
          >
            + New Category
          </button>
        )}
        
        {getSortedCategories().map((category) => {
          const categoryChannels = channelsByCategory[category];
          const isCollapsed = collapsedCategories[category];
          
          return (
            <div 
              key={category} 
              className="channel-category"
              onDragOver={(e) => handleDragOverCategory(e, category)}
              onDrop={(e) => handleDrop(e, null, category)}
            >
              {editingCategory === category ? (
                <div className="category-edit">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleRenameCategory(category)}
                    onBlur={() => handleRenameCategory(category)}
                    autoFocus
                  />
                </div>
              ) : (
                <div 
                  className={`category-header ${dragOverCategory === category ? 'drag-over' : ''}`}
                  onClick={() => toggleCategory(category)}
                  onContextMenu={(e) => handleCategoryRightClick(e, category)}
                >
                  <span className="category-icon">{isCollapsed ? '►' : '▼'}</span>
                  <span className="category-name">{category}</span>
                  {canManageChannels && (
                    <button 
                      className="add-channel-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddChannel(category);
                      }}
                      title="Add Channel"
                    >
                      +
                    </button>
                  )}
                </div>
              )}
              
              {!isCollapsed && categoryChannels.map(channel => (
                <div key={channel.id}>
                  {editingChannel === channel.id ? (
                    <div className="channel-edit">
                      <input
                        type="text"
                        value={newChannelName}
                        onChange={(e) => setNewChannelName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleRenameChannel(channel.id)}
                        onBlur={() => handleRenameChannel(channel.id)}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div
                      className={`channel-item ${channel.type === 'voice' ? 'voice' : ''} ${currentChannel?.id === channel.id ? 'active' : ''} ${unreadCounts[channel.id] > 0 && !mentionCounts[channel.id] && currentChannel?.id !== channel.id ? 'has-unread' : ''} ${draggingChannel?.id === channel.id ? 'dragging' : ''} ${dragOverChannel?.id === channel.id ? 'drag-over' : ''}`}
                      draggable={canManageChannels}
                      onDragStart={(e) => handleDragStart(e, channel)}
                      onDragOver={(e) => handleDragOver(e, channel)}
                      onDrop={(e) => handleDrop(e, channel, category)}
                      onDragEnd={handleDragEnd}
                      onClick={() => onSelectChannel(channel)}
                      onContextMenu={(e) => handleChannelRightClick(e, channel)}
                    >
                      <span className="channel-icon">{channel.type === 'voice' ? '🔊' : '#'}</span>
                      <span className="channel-name">{channel.name}</span>
                      {mentionCounts[channel.id] > 0 && currentChannel?.id !== channel.id && (
                        <span className="mention-badge">{mentionCounts[channel.id]}</span>
                      )}
                    </div>
                  )}
                  
                  {channel.type === 'voice' && voiceUsers[channel.id] && voiceUsers[channel.id].length > 0 && (
                    <div className="voice-members-sidebar">
                      {voiceUsers[channel.id]
                        .filter(voiceUser => voiceUser.userId && voiceUser.username)
                        .map(voiceUser => (
                          <VoiceMemberItem
                            key={voiceUser.userId}
                            voiceUser={voiceUser}
                            user={user}
                            voiceAudioControls={voiceAudioControls}
                          />
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}

      {showInviteModal && (
        <InviteModal 
          server={server} 
          user={user}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {showServerSettings && (
        <ServerSettings 
          server={server}
          onClose={() => setShowServerSettings(false)}
          token={localStorage.getItem('token')}
          onServerUpdate={(updatedServer) => {
            // Server will be updated through socket or parent component refresh
            setShowServerSettings(false);
          }}
        />
      )}

      {showUserProfileEdit && (
        <UserProfileEdit
          user={user}
          onClose={() => setShowUserProfileEdit(false)}
          token={localStorage.getItem('token')}
          onUpdate={(updatedUser) => {
            // Update user prop directly without reload
            if (typeof user === 'object') {
              Object.keys(updatedUser).forEach(key => {
                user[key] = updatedUser[key];
              });
            }
            setShowUserProfileEdit(false);
          }}
        />
      )}

      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}

      {showChannelPermissions && selectedChannel && server && (
        <ChannelPermissions
          channel={selectedChannel}
          server={server}
          token={localStorage.getItem('token')}
          onClose={() => {
            setShowChannelPermissions(false);
            setSelectedChannel(null);
          }}
        />
      )}

      <div className="user-panel">
        <div 
          className="user-info clickable" 
          onClick={() => setShowUserProfileEdit(true)}
          title="Click to edit profile"
        >
          <div className="user-avatar">
            {user?.avatar_url ? (
              <img 
                src={`${process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : window.location.hostname.includes('ripkord.kew.bar') ? 'https://api.ripkord.kew.bar' : `${window.location.protocol}//${window.location.hostname}:3001`)}${user.avatar_url}`}
                alt={user.username}
              />
            ) : (
              user?.username?.substring(0, 1).toUpperCase()
            )}
          </div>
          <div className="user-details">
            <div className="user-name">{user?.username}</div>
            <div className="user-status">
              {user?.status === 'online' && '🟢 Online'}
              {user?.status === 'idle' && '🟡 Idle'}
              {user?.status === 'dnd' && '🔴 Do Not Disturb'}
              {user?.status === 'invisible' && '⚫ Invisible'}
              {!user?.status && 'Online'}
            </div>
          </div>
        </div>
        <div className="user-controls">
          <button className="control-button settings-button" onClick={() => setShowSettings(true)} title="Voice & Video Settings">
            ⚙️
          </button>
          <button className="control-button logout-button" onClick={onLogout} title="Logout (Clear Session)">
            🚪
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChannelList;
